import React, { FC, useContext } from 'react';
import {
  VehicleOptionsResponse,
  DriverType,
  VehicleType,
} from '@clearcover/core-app-types';
import { CardSectionInfoPairs } from 'Shared/components/Card/CardSectionInfoPairs';
import { CardDataSection } from 'Shared/components/Card/CardDataSection';

import styles from './styles.module.css';
import {
  combineStrings,
  formatNumberWithCommas,
  formatAddress,
  EMDASH,
  booleanValueMapping,
} from 'Shared/utils';
import { MultilineAddress } from '../Address';
import { PolicyContext } from 'Policies/utils';
import { useApi } from 'Shared/services/Api';
import { ModalLoading } from '@clearcover/atlas';

type PolicyVehicleDetailsType = {
  vehicle: VehicleType;
  drivers: Array<DriverType>;
  showAntiTheftDevice: boolean;
};

const GENERAL_INFO_HEADERS = {
  GARAGING_ADDRESS: 'Garaging Address',
  VIN: 'Identification number (VIN)',
  ANTI_THEFT: 'Anti-theft device',
  BUSINESS_USE: 'Business Use',
};

const OWNERSHIP_HEADERS = {
  REGISTERED_TO_APPLICANT_OR_SPOUSE: 'Registered to applicant/spouse?',
};

export const PolicyVehicleDetails: FC<PolicyVehicleDetailsType> = ({
  vehicle: {
    year,
    make,
    model,
    bodyStyle,
    vin,
    antiTheft,
    lienholder: { addressFull, name },
    garagingAddress,
    ownerShipType,
    usage,
    costNewAmount,
    statedValue,
    assignedDrivers,
    registeredToPolicyholderOrSpouse,
  },
  drivers,
  showAntiTheftDevice,
}) => {
  const { details } = useContext(PolicyContext);

  const [{ data, loading: isLoading, error }] = useApi<VehicleOptionsResponse>(
    `/options/vehicle?identifier=${details.filingIdentifier}`
  );

  if (isLoading) {
    return <ModalLoading data-testid="vehicle-details-loading" />;
  }

  const rideshareOffered = error
    ? true
    : data.endorsements
        .map(endorsement => endorsement.identifier)
        .includes('rideshare');
  const showGaragingAddress = error ? false : data.showGaragingAddress;

  const hiddenGeneralInfoColumns = [
    ...(showGaragingAddress ? [] : [GENERAL_INFO_HEADERS.GARAGING_ADDRESS]),
    ...(showAntiTheftDevice ? [] : [GENERAL_INFO_HEADERS.ANTI_THEFT]),
    ...(usage.showBusinessUseDetail ? [GENERAL_INFO_HEADERS.BUSINESS_USE] : []),
  ];

  const hiddenOwnershipColumns = [
    ...(details.showRegisteredToApplicantOrSpouse
      ? []
      : [OWNERSHIP_HEADERS.REGISTERED_TO_APPLICANT_OR_SPOUSE]),
  ];

  return (
    <div className={styles.detailsContainer}>
      <CardDataSection caption="General Info" id="general-info">
        <CardSectionInfoPairs
          headers={['Year', 'Make/Manufacturer', 'Model', 'Body Style']}
          data={[year, make, model, bodyStyle]}
        />
        <span className="fs-exclude">
          <CardSectionInfoPairs
            headers={[
              GENERAL_INFO_HEADERS.GARAGING_ADDRESS,
              GENERAL_INFO_HEADERS.VIN,
              GENERAL_INFO_HEADERS.ANTI_THEFT,
              GENERAL_INFO_HEADERS.BUSINESS_USE,
            ]}
            hiddenColumns={hiddenGeneralInfoColumns}
            data={[
              {
                display: <MultilineAddress address={garagingAddress} />,
                value: formatAddress(garagingAddress),
              },
              vin,
              antiTheft.hasAntiTheftDevice ? 'Yes' : 'No',
              usage.business
                ? usage.unacceptableBusinessUse
                  ? 'Unacceptable'
                  : 'Acceptable'
                : 'No',
            ]}
          />
        </span>
      </CardDataSection>
      {usage.showBusinessUseDetail && (
        <CardDataSection caption="Usage" id="usage">
          <CardSectionInfoPairs
            headers={['Yearly Mileage', 'Usage', 'Rideshare']}
            hiddenColumns={rideshareOffered ? [] : ['Rideshare']}
            data={[
              usage.yearlyMileage
                ? formatNumberWithCommas(usage.yearlyMileage)
                : '',
              usage.use,
              usage.rideShare ? 'YES' : 'NO',
            ]}
          />
        </CardDataSection>
      )}
      <CardDataSection caption="Ownership" id="ownership">
        <span className="fs-exclude">
          <CardSectionInfoPairs
            hiddenColumns={hiddenOwnershipColumns}
            headers={[
              OWNERSHIP_HEADERS.REGISTERED_TO_APPLICANT_OR_SPOUSE,
              'Ownership Type',
              'Lienholder Name',
              'Lienholder address',
            ]}
            data={[
              typeof registeredToPolicyholderOrSpouse === 'boolean'
                ? booleanValueMapping[
                    registeredToPolicyholderOrSpouse.toString()
                  ]
                : '-',
              ownerShipType,
              name,
              addressFull,
            ]}
          />
        </span>
      </CardDataSection>
      {details.region === 'US-VA' ? (
        <CardDataSection caption="Assigned Drivers" id="assigned-drivers">
          <div className={styles.combined}>
            {drivers
              .filter(d => assignedDrivers.includes(d.id))
              .map(({ person }) =>
                combineStrings([
                  person.name.firstName,
                  person.name.middleName,
                  person.name.lastName,
                  person.suffix,
                ])
              )
              .join(', ')}
          </div>
        </CardDataSection>
      ) : null}
      {details?.showPricingData && (
        <CardDataSection
          caption="Pricing Data"
          id="pricing-data"
          lastSection={true}
        >
          <CardSectionInfoPairs
            headers={['Cost new', 'Stated value', 'Trade-in']}
            data={[costNewAmount, statedValue, EMDASH]}
          />
        </CardDataSection>
      )}
    </div>
  );
};
